import { profileFetch } from "../profileSession";
import { API_BASE, ApiError, apiAssetUrl, getToken, downloadApiFile, req, privateDocumentBlob, uploadProfessionalForm } from "./transport";
import type { JobseekerDiscovery, ProfessionalReviewStatus, AdminProfessionalRequestSummary, AdminProfessionalRequest, ProfessionalRequestSummary, ProfessionalMemberRequest, ProfessionalSubmissionInput, ProfessionalAdditionInput } from "./types";

export type Availability = { status: string; slots: { id: string; dayOfWeek: number; startTime: string; endTime: string }[] };
export type IdentityDocument = "id_image" | "selfie" | "address_proof";
export type IdentityVerification = {
  status: string;
  idType?: string | null;
  idNumber?: string | null;
  decisionNote?: string | null;
  reviewedAt?: string | null;
  docs: Record<IdentityDocument, boolean>;
};

export const profilesApi = {
  getJobseekerDiscovery() {
    return req<JobseekerDiscovery>("/profile/me/jobseeker-discovery", {}, { rejectUnauthorized: true });
  },

  setJobseekerDiscovery(enabled: boolean) {
    return req<JobseekerDiscovery>("/profile/me/jobseeker-discovery", {
      method: "PATCH", body: JSON.stringify({ enabled }),
    }, { rejectUnauthorized: true });
  },

  // v3.9.66 (Stage 6 / H) — my account standing (status + warnings received)
  async myStanding() {
    return req<{ status: string; standing: "suspended" | "attention" | "trusted" | "good"; warningCount: number; verified: boolean; verifiedKind: string | null; workerVerified: boolean; identityVerified: boolean; warnings: { id: string; reason: string; note: string | null; createdAt: string }[] }>("/profile/me/standing");
  },

  // v3.9.39 (CEO #8) — profile avatar + banner upload
  async uploadProfileImage(kind: "avatar" | "banner", blob: Blob) {
    const token = getToken();
    const res = await profileFetch(`${API_BASE}/profile/me/${kind}`, {
      method: "POST", credentials: "include",
      headers: { "Content-Type": (blob as any).type || "image/jpeg", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: blob,
    });
    const data = await res.json();
    if (!res.ok) throw new Error((data as any)?.error || `Upload failed (${res.status})`);
    return data as { ok: boolean; url: string };
  },

  async removeProfileBanner() {
    return req<{ ok: boolean }>("/profile/me/banner", { method: "DELETE" });
  },

  // v3.9.40 (CEO #7) — resume attachments (multiple, one active)
  async myResumes() { return req<{ attachments: any[] }>("/profile/me/resumes"); },

  async uploadResume(file: File) {
    const token = getToken();
    const res = await profileFetch(`${API_BASE}/profile/me/resume`, {
      method: "POST", credentials: "include",
      headers: {
        "Content-Type": file.type || "application/octet-stream",
        "X-File-Name": encodeURIComponent(file.name || "resume"),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: file,
    });
    const data = await res.json();
    if (!res.ok) throw new Error((data as any)?.error || `Upload failed (${res.status})`);
    return data as { attachment: any };
  },

  async setActiveResume(id: string, confirmJobseekerDisable = false) { return req<{ ok: boolean }>(`/profile/me/resume/${id}/active`, { method: "POST", body: JSON.stringify({ confirmJobseekerDisable }) }); },

  async deleteResume(id: string, confirmJobseekerDisable = false) { return req<{ ok: boolean }>(`/profile/me/resume/${id}`, { method: "DELETE", body: JSON.stringify({ confirmJobseekerDisable }) }); },

  /** URL for rendering a publicly shared résumé in the in-app viewer. */
  viewResumeAttachmentUrl(ownerUserId: number, attachmentId: string) {
    return `${API_BASE}/profile/${encodeURIComponent(ownerUserId)}/resume/${encodeURIComponent(attachmentId)}?disposition=inline`;
  },

  async downloadResumeAttachment(ownerUserId: number, attachmentId: string) {
    return downloadApiFile(`/profile/${encodeURIComponent(ownerUserId)}/resume/${encodeURIComponent(attachmentId)}`,
      `resume-${attachmentId}`, "Could not download the résumé file.");
  },

  // Increment 14: profiles + verification
  async getProfile(userId: number, signal?: AbortSignal) {
    return req<{ user: any; rating: any; reviews: any[]; listings: any; counts: any }>(`/profile/${userId}`, { signal }, { rejectUnauthorized: signal !== undefined });
  },

  async getMyProfileVisibility() {
    return req<{ profilePublic: boolean }>("/profile/me/visibility");
  },

  async setMyProfileVisibility(profilePublic: boolean) {
    return req<{ profilePublic: boolean }>("/profile/me/visibility", { method: "PATCH", body: JSON.stringify({ profilePublic }) });
  },

  async myVerification() {
    return req<{ verified: boolean; verifiedKind: string | null; request: any | null }>("/verification/me");
  },

  async submitVerification(kind: "id" | "company", label?: string) {
    return req<{ request: any }>("/verification", { method: "POST", body: JSON.stringify({ kind, label }) });
  },

  async pendingVerifications() {
    const r = await req<{ pending: any[] }>("/verification/pending");
    return r?.pending ?? [];
  },

  // Increment 25: Worker KYC (worker side)
  async workerKyc() {
    return req<{ workerVerified: boolean; verification: IdentityVerification }>("/workers/verification/me", {}, { rejectUnauthorized: true });
  },

  async startWorkerKyc(idType: string, idNumber: string, signal?: AbortSignal) {
    return req<{ verification: IdentityVerification }>("/workers/verification", { method: "POST", body: JSON.stringify({ idType, idNumber }), signal }, { rejectUnauthorized: true });
  },

  async uploadKycDoc(docType: IdentityDocument, file: File, signal?: AbortSignal) {
    const token = getToken();
    const res = await profileFetch(`${API_BASE}/workers/verification/doc/${docType}`, {
      method: "POST", credentials: "include",
      headers: { "Content-Type": file.type || "application/octet-stream", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: file, signal,
    });
    if (res.status === 401) throw new ApiError(401, "Sign in again to upload your document.");
    const documentResponse = await res.json();
    if (!res.ok) throw new ApiError(res.status, typeof documentResponse?.error === "string" ? documentResponse.error : "Could not upload the document. Try again.");
    return documentResponse as { verification: IdentityVerification };
  },

  async submitWorkerKyc(signal?: AbortSignal) {
    return req<{ verification: IdentityVerification }>("/workers/verification/submit", { method: "POST", signal }, { rejectUnauthorized: true });
  },

  // Increment 25: Worker KYC (admin side)
  async adminWorkerVerifications(status = "pending") {
    const r = await req<{ verifications: any[] }>(`/admin/worker-verifications?status=${encodeURIComponent(status)}`);
    return r?.verifications ?? [];
  },

  async adminApproveWorkerVerification(id: string) {
    return req<{ ok: boolean }>(`/admin/worker-verifications/${encodeURIComponent(id)}/approve`, { method: "POST" });
  },

  async adminRejectWorkerVerification(id: string, note?: string) {
    return req<{ ok: boolean }>(`/admin/worker-verifications/${encodeURIComponent(id)}/reject`, { method: "POST", body: JSON.stringify({ note }) });
  },

  /** Fetch a KYC document as an object URL (carries the admin Bearer token). */
  async adminKycDocUrl(id: string, docType: "id_image" | "selfie" | "address_proof"): Promise<string | null> {
    const token = getToken();
    const res = await profileFetch(`${API_BASE}/admin/worker-verifications/${encodeURIComponent(id)}/doc/${docType}`, {
      credentials: "include", headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    });
    if (!res.ok) return null;
    const blob = await res.blob();
    return URL.createObjectURL(blob);
  },

  // Increment 27: worker onboarding + profile
  async workerProfile(signal?: AbortSignal) {
    return req<{ profile: any; completeness: number; missing: string[]; onboarded: boolean; personalLock: { locked: boolean; until: string | null; fields: string[] }; minDailyRate: number; maxYearsExperience?: number }>("/workers/profile/me", { signal }, { rejectUnauthorized: signal !== undefined });
  },

  async updateWorkerProfile(patch: Record<string, unknown>) {
    return req<{ profile: any; completeness: number; missing: string[]; onboarded: boolean; personalLock: any; minDailyRate: number; maxYearsExperience?: number }>("/workers/profile", { method: "PUT", body: JSON.stringify(patch) });
  },

  async completeWorkerOnboarding(payload: Record<string, unknown>) {
    return req<{ profile: any; completeness: number; onboarded: boolean; personalLock: any; maxYearsExperience?: number }>("/workers/onboarding/complete", { method: "POST", body: JSON.stringify(payload) });
  },

  // Increment 46a: worker payout account + earnings
  async myBankAccount() {
    return req<{ account: any | null }>("/workers/me/bank-account");
  },

  async saveBankAccount(input: { bankCode: string; accountName: string; accountNumber: string; ownershipConfirmed: boolean }) {
    return req<{ ok: boolean; payoutAccountChanged: boolean; message: string; account: any }>("/workers/me/bank-account", { method: "PUT", body: JSON.stringify(input) });
  },

  async myEarnings(signal?: AbortSignal) {
    return req<{ availableBalance: number; pendingRelease: number; releasedTotal: number; weeklyEarnings: number; monthlyEarnings: number; earningsHistory: { label: string; value: number }[]; recentPayments: any[]; bankAccount: any | null }>("/workers/me/earnings", { signal }, { rejectUnauthorized: signal !== undefined });
  },

  // Batch item 2: worker portfolio / media
  async myMedia() {
    const r = await req<{ media: any[] }>("/workers/me/media");
    return r?.media ?? [];
  },

  async workerMedia(userId: number) {
    const r = await req<{ media: any[] }>(`/workers/${userId}/media`);
    return r?.media ?? [];
  },

  // Portfolio media url already begins with /api/... ; prefix only the origin.
  workerMediaSrc(url: string) {
    return apiAssetUrl(url) || url;
  },

  portfolioMediaBlob(id: string, signal?: AbortSignal) {
    return privateDocumentBlob(`/workers/media/${encodeURIComponent(id)}/image`, signal);
  },

  async uploadMedia(file: File, meta?: { title?: string; caption?: string }) {
    const token = getToken();
    const qs = new URLSearchParams();
    if (meta?.title) qs.set("title", meta.title);
    if (meta?.caption) qs.set("caption", meta.caption);
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    const res = await profileFetch(`${API_BASE}/workers/me/media${suffix}`, {
      method: "POST", credentials: "include",
      headers: { "Content-Type": file.type || "application/octet-stream", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: file,
    });
    const data = await res.json();
    if (!res.ok) throw new Error((data as any)?.error || `Upload failed (${res.status})`);
    return data as { media: any };
  },

  async updateMedia(id: string, meta: { title?: string; caption?: string }) {
    return req<{ media: any }>(`/workers/me/media/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(meta) });
  },

  async deleteMedia(id: string) {
    return req<{ ok: boolean }>(`/workers/me/media/${encodeURIComponent(id)}`, { method: "DELETE" });
  },

  // Batch item 4: verification-queue position
  async myVerificationQueue() {
    return req<{ inQueue: boolean; status: string; position: number | null; total: number; submittedAt?: string }>("/workers/me/verification-queue");
  },

  // Increment 28: worker availability
  async myAvailability() {
    return req<Availability>("/workers/availability/me", {}, { rejectUnauthorized: true });
  },

  saveAvailabilitySchedule(slots: Omit<Availability["slots"][number], "id">[], expectedSlotIds: string[]) {
    return req<Availability>("/workers/availability/schedule", {
      method: "PUT", body: JSON.stringify({ slots, expectedSlotIds }),
    }, { rejectUnauthorized: true });
  },

  async setAvailabilityStatus(status: "available" | "busy" | "unavailable") {
    return req<Availability>("/workers/availability/status", { method: "PUT", body: JSON.stringify({ status }) }, { rejectUnauthorized: true });
  },

  async addAvailabilitySlot(dayOfWeek: number, startTime: string, endTime: string) {
    return req<{ status: string; slots: any[] }>("/workers/availability", { method: "POST", body: JSON.stringify({ dayOfWeek, startTime, endTime }) });
  },

  async addAvailabilitySlots(dayOfWeeks: number[], startTime: string, endTime: string) {
    return req<{ status: string; slots: any[] }>("/workers/availability/batch", { method: "POST", body: JSON.stringify({ dayOfWeeks, startTime, endTime }) });
  },

  async deleteAvailabilitySlot(id: string) {
    return req<{ status: string; slots: any[] }>(`/workers/availability/${encodeURIComponent(id)}`, { method: "DELETE" });
  },

  // Increment 26: worker credentials (worker side)
  async myCredentials() {
    const r = await req<{ credentials: any[] }>("/workers/credentials");
    return r?.credentials ?? [];
  },

  async addCredential(input: { credentialType: string; credentialNumber?: string; issuer?: string; issueDate?: string; expiryDate?: string }) {
    return req<{ credential: any }>("/workers/credentials", { method: "POST", body: JSON.stringify(input) });
  },

  async uploadCredentialDoc(id: string, file: File) {
    const token = getToken();
    const res = await profileFetch(`${API_BASE}/workers/credentials/${encodeURIComponent(id)}/doc`, {
      method: "POST", credentials: "include",
      headers: { "Content-Type": file.type || "application/octet-stream", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: file,
    });
    const data = await res.json();
    if (!res.ok) throw new Error((data as any)?.error || `HTTP ${res.status}`);
    return data;
  },

  async deleteCredential(id: string) {
    return req<{ ok: boolean }>(`/workers/credentials/${encodeURIComponent(id)}`, { method: "DELETE" });
  },

  // Increment 26: worker credentials (admin side)
  async adminWorkerCredentials(status = "pending") {
    const r = await req<{ credentials: any[] }>(`/admin/worker-credentials?status=${encodeURIComponent(status)}`);
    return r?.credentials ?? [];
  },

  async adminApproveCredential(id: string) {
    return req<{ ok: boolean }>(`/admin/worker-credentials/${encodeURIComponent(id)}/approve`, { method: "POST" });
  },

  async adminRejectCredential(id: string, note?: string) {
    return req<{ ok: boolean }>(`/admin/worker-credentials/${encodeURIComponent(id)}/reject`, { method: "POST", body: JSON.stringify({ note }) });
  },

  async adminCredentialDocUrl(id: string): Promise<string | null> {
    const token = getToken();
    const res = await profileFetch(`${API_BASE}/admin/worker-credentials/${encodeURIComponent(id)}/doc`, {
      credentials: "include", headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    });
    if (!res.ok) return null;
    return URL.createObjectURL(await res.blob());
  },

  async approveVerification(id: string) {
    return req<{ ok: boolean; status: string }>(`/verification/${encodeURIComponent(id)}/approve`, { method: "POST" });
  },

  async rejectVerification(id: string, note?: string) {
    return req<{ ok: boolean; status: string }>(`/verification/${encodeURIComponent(id)}/reject`, { method: "POST", body: JSON.stringify({ note }) });
  },

  async myProfessionalRequests() {
    return req<{ requests: ProfessionalRequestSummary[] }>("/professional/requests");
  },

  async myProfessionalRequest(id: string) {
    return req<{ request: ProfessionalMemberRequest }>(`/professional/requests/${encodeURIComponent(id)}`);
  },

  async submitProfessionalRequest(input: ProfessionalSubmissionInput) {
    return uploadProfessionalForm<{ request: ProfessionalMemberRequest }>("/professional/requests", "POST", input);
  },

  async correctProfessionalRequest(id: string, input: ProfessionalSubmissionInput) {
    return uploadProfessionalForm<{ request: ProfessionalMemberRequest }>(
      `/professional/requests/${encodeURIComponent(id)}`, "PUT", input,
    );
  },

  async addProfessionalCredentials(id: string, input: ProfessionalAdditionInput) {
    return uploadProfessionalForm<{ request: ProfessionalMemberRequest }>(
      `/professional/requests/${encodeURIComponent(id)}/credentials`, "POST", input,
    );
  },

  async adminProfessionalRequests(status?: ProfessionalReviewStatus, limit = 50, offset = 0, userId?: number) {
    return req<{ requests: AdminProfessionalRequestSummary[]; total: number; limit: number; offset: number }>(
      `/admin/professional/requests?needsReview=${status ? "false" : "true"}${status ? `&status=${status}` : ""}${userId ? `&userId=${userId}` : ""}&limit=${limit}&offset=${offset}`,
    );
  },

  async adminProfessionalRequest(id: string) {
    return req<{ request: AdminProfessionalRequest }>(`/admin/professional/requests/${encodeURIComponent(id)}`);
  },

  async decideProfessionalRequest(id: string, input: {
    decision: "approve" | "reject"; reason?: string;
    credentialExpiries?: { credentialId: string; expiryDate: string }[];
    credentialDecisions?: ({ credentialId: string; decision: "approve"; expiryDate?: string } | { credentialId: string; decision: "reject"; reason: string })[];
  }) {
    return req<{ request: AdminProfessionalRequest }>(`/admin/professional/requests/${encodeURIComponent(id)}/decision`, {
      method: "POST", body: JSON.stringify(input),
    });
  },

  async decideProfessionalCredential(requestId: string, credentialId: string, input: {
    decision: "approve" | "reject"; reason?: string; expiryDate?: string;
  }) {
    return req<{ request: AdminProfessionalRequest }>(
      `/admin/professional/requests/${encodeURIComponent(requestId)}/credentials/${encodeURIComponent(credentialId)}/decision`,
      { method: "POST", body: JSON.stringify(input) },
    );
  },

  async revokeProfessionalRequest(id: string, reason?: string) {
    return req<{ request: AdminProfessionalRequest }>(`/admin/professional/requests/${encodeURIComponent(id)}/revoke`, {
      method: "POST", body: JSON.stringify({ reason }),
    });
  },

  async professionalEvidenceBlob(requestId: string, imageId: string): Promise<Blob> {
    const token = getToken();
    const response = await profileFetch(`${API_BASE}/professional/requests/${encodeURIComponent(requestId)}/evidence/${encodeURIComponent(imageId)}`, {
      credentials: "include",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error((error as { error?: string }).error || `HTTP ${response.status}`);
    }
    return response.blob();
  }
};
